// src/components/aols/privacy/license/License.tsx
import React, { useCallback } from "react";
import styled from "styled-components";
import { Layout, Typography } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "/logo.jpg";
import { Footer } from "../../../dev/utils/Footer";
import { HomeOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

interface LicenseProps {}

const FullHeightLayout = styled(Layout)`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #000000 100%);
  padding: 20px;
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important;
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important;
  }

  .breadcrumb-separator {
    color: #fff;
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px;
  background: #111;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  color: #fff;

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 30px;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255, 204, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const MainTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const MainTitleButton = styled(Link)`
  background: #ffaa00;
  color: #0427f4;
  font-size: 24px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 6px 12px;
  }
`;

const LinkStyled = styled.a`
  color: #ffaa00;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const SectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 30px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const PolicyText = styled(Paragraph)`
  line-height: 1.8;
  color: #ddd;
  margin-bottom: 15px;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const List = styled.ul`
  margin-left: 20px;
  list-style-type: disc;

  li {
    margin: 5px 0;
    color: #ddd;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;

const License: React.FC<LicenseProps> = () => {
  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    <FullHeightLayout>
      <Helmet>
        <title>Proprietary License - OneLightSystem OLS</title>
        <meta
          name="description"
          content="Review the Proprietary License for OneLightSystem OLS, governing the use of olsme.com's software, data, and services for meditation education."
        />
        <meta
          name="keywords"
          content="Proprietary License, OneLightSystem OLS, OLS Software, Meditation Education, Sun Light Civilization, OLSME, OLSLM"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Proprietary License - OneLightSystem OLS" />
        <meta
          property="og:description"
          content="Learn about the Proprietary License for OneLightSystem OLS, detailing terms for using olsme.com's software and services."
        />
        <meta property="og:url" content="https://www.olsme.com/license" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Proprietary License - OneLightSystem OLS" />
        <meta
          property="twitter:description"
          content="Explore the Proprietary License for OneLightSystem OLS, governing the use of software and services for meditation education."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/license" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Proprietary License - OneLightSystem OLS",
            description:
              "Proprietary License for OneLightSystem OLS, governing the use of olsme.com's software, data, and services for meditation education.",
            url: "https://www.olsme.com/license",
            publisher: {
              "@type": "Organization",
              name: "OneLightSystem OLS",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "info@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "2033 Ralston Ave",
                addressLocality: "Belmont",
                addressRegion: "CA",
                postalCode: "94002",
                addressCountry: "US",
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                jobTitle: "CEO",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Proprietary License",
                  item: "https://www.olsme.com/license",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            Proprietary License
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>
      <ContentWrapper>
        <LinkStyled href="https://www.olsme.com" aria-label="Go to OneLightSystem OLS homepage">
          <LogoImg src={logo} alt="OneLightSystem OLS Logo" onClick={handleLogoClick} />
        </LinkStyled>
        <MainTitleWrapper>
          <MainTitleButton to="/LightScience" aria-label="Go to LightScience page">
            onelightsystem
          </MainTitleButton>
          <MainTitleButton to="/OLSaeo" aria-label="Go to OLS iee.aeo page">
            OLS iee.aeo
          </MainTitleButton>
          <MainTitleButton to="/privacy" aria-label="Go to Privacy Policy page">
            Privacy Policy
          </MainTitleButton>
          <MainTitleButton to="/olsdataprovision" aria-label="Go to OLS Data Provision page">
            OLS Data Provision
          </MainTitleButton>
          <Title level={1} className="main-title-text" style={{ color: "#ffffff" }}>
            Proprietary License
          </Title>
        </MainTitleWrapper>

        <PolicyText>
          <strong>OneLightSystem OLS Proprietary License</strong>
          <br />
          Copyright © 2025 OneLightSystem OLS, LLC. All Rights Reserved.
          <br />
          Version: 1.0
          <br />
          Last Updated: April 18, 2025
          <br />
          © OneLightSystem OLS, LLC | OLS iee.aeo | CEO: Nazar Pankiv
        </PolicyText>

        <SectionTitle level={2}>1. Scope and Restrictions</SectionTitle>
        <PolicyText>
          This software, including its code, data, designs, and associated materials (collectively, “Software”), is the proprietary and confidential property of OneLightSystem OLS, LLC (“OLS”). Unauthorized actions are strictly prohibited without prior written consent from OLS, including but not limited to:
        </PolicyText>
        <List>
          <li>Copying or reproducing the Software.</li>
          <li>Redistributing or sharing the Software.</li>
          <li>Modifying or adapting the Software.</li>
          <li>Reverse engineering, decompiling, or deriving source code.</li>
          <li>Creating derivative works based on the Software.</li>
        </List>

        <SectionTitle level={2}>2. Permitted Use</SectionTitle>
        <PolicyText>
          OLS grants you a limited, non-exclusive, non-transferable license to access and interact with the Software solely for engaging with olsme.com’s educational and meditation services, such as OLS student registration, OLSME subscriptions, and OLSLM asset management. No additional rights are granted unless explicitly authorized in writing by OLS.
        </PolicyText>

        <SectionTitle level={2}>3. Ownership and Intellectual Property</SectionTitle>
        <PolicyText>
          All intellectual property rights, including copyrights, trademarks (e.g., “OLS®”), and innovations (e.g., Light Calendar, Light Time), remain the exclusive property of OLS. This license does not transfer any ownership or rights to the Software or its components.
        </PolicyText>

        <SectionTitle level={2}>4. Disclaimer of Warranties</SectionTitle>
        <PolicyText>
          The Software is provided “as is,” without warranties of any kind, express or implied, including but not limited to merchantability, fitness for a particular purpose, or non-infringement. OLS is not liable for any damages, losses, or issues arising from the use or inability to use the Software.
        </PolicyText>

        <SectionTitle level={2}>5. Termination</SectionTitle>
        <PolicyText>
          Any violation of this license’s terms will result in immediate termination of your access rights. OLS reserves the right to pursue legal remedies for unauthorized use or breach of this agreement.
        </PolicyText>

        <SectionTitle level={2}>6. Contact Information</SectionTitle>
        <PolicyText>
          For inquiries, permissions, or licensing requests, please visit:
          <br />
          <LinkStyled href="/contact" aria-label="Visit OLS Contact page">
            olsme.com/contact
          </LinkStyled>
          <br />
          Email:{" "}
          <LinkStyled href="mailto:info@olsme.com" aria-label="Email info@olsme.com">
            info@olsme.com
          </LinkStyled>
        </PolicyText>

        <SectionTitle level={2}>7. Governing Law</SectionTitle>
        <PolicyText>
          This license is governed by the laws of the State of California, USA, without regard to conflict of law principles. Any disputes will be resolved in the state or federal courts of California.
        </PolicyText>
      </ContentWrapper>
      <Footer />
    </FullHeightLayout>
  );
};

export default License;